/**
 * Settings -> Integrations -> when the phone is answered.
 *
 * Its own tab rather than a block under Inbound, and the split is the same one
 * that put Inbound beside Telephony: everything here changes what a customer
 * hears, and nothing here is a credential. The Inbound tab answers "what happens
 * when somebody rings"; this one answers "and what if they ring at three in the
 * morning".
 *
 * The card asks the server what the schedule on screen would MEAN on every edit
 * (`previewHours`). Three of the things it shows cannot be worked out in the
 * browser without a second copy of the server's rules - the clock in the chosen
 * zone, the spoken sentence, and whether the wording still names the old hours -
 * and a second copy is the mistake `isSayLanguage` already documents.
 */
import { useCallback, useEffect, useState } from "react";
import { TimezoneCombobox } from "@wa/ui";
import { CalendarClock, RefreshCw, Save, Wand2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getTelephonySettings,
  previewHours,
  saveTelephonySettings,
  type BusinessDay,
  type HoursPreview,
  type NoticeVariant,
  type TelephonyStatus,
} from "../../../../lib/calls";
import { formatPhone } from "../../../../lib/phone";
import { Btn, StatusPill } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, SaveRow, ToggleRow } from "../panel/fields";
import { HoursSchedule } from "./HoursSchedule";
import { defaultWeek, normaliseWeek } from "./hoursWeek";
import { NoticeVariantFields } from "./NoticeVariantFields";

const EMPTY: NoticeVariant = { audioUrl: "", text: "" };

/** How long the form sits still before it asks the server what it now means. */
const PREVIEW_DEBOUNCE_MS = 350;

export function HoursCard() {
  const t = useT();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState("Europe/Tallinn");
  const [days, setDays] = useState<BusinessDay[]>(defaultWeek());
  const [voicemail, setVoicemail] = useState(true);
  const [voicemailPrompt, setVoicemailPrompt] = useState<NoticeVariant>(EMPTY);
  const [closedPrompt, setClosedPrompt] = useState<NoticeVariant>(EMPTY);
  const [preview, setPreview] = useState<HoursPreview | null>(null);

  const hydrate = useCallback((data: TelephonyStatus) => {
    setStatus(data);
    const hours = data.inbound.hours;
    setEnabled(hours.enabled);
    setTimezone(hours.timezone);
    setDays(normaliseWeek(hours.days));
    setVoicemail(hours.voicemail);
    setVoicemailPrompt({ ...hours.voicemailPrompt });
    setClosedPrompt({ ...hours.closedPrompt });
  }, []);

  useEffect(() => {
    getTelephonySettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hydrate]);

  // The wording of the moment: only the prompt that would really play is on
  // screen, so only that one is worth asking the drift question about.
  const prompt = voicemail ? voicemailPrompt : closedPrompt;
  const setPrompt = voicemail ? setVoicemailPrompt : setClosedPrompt;
  const slot = voicemail ? ("hours.voicemail" as const) : ("hours.closed" as const);

  // Debounced so that dragging through a select does not fire a request per
  // option. The dependency is the serialised form rather than the objects, which
  // are rebuilt on every keystroke and would make an effect on them run forever.
  const shape = JSON.stringify({ timezone, days, voicemail, prompt });
  useEffect(() => {
    if (loading) return undefined;
    const timer = setTimeout(() => {
      previewHours({
        timezone,
        days,
        voicemail,
        voicemailPrompt,
        closedPrompt,
      })
        .then(setPreview)
        // A preview that fails costs an explanation, never the form: everything
        // it answers is advisory, and the schedule is still saveable without it.
        .catch(() => setPreview(null));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, loading]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      hydrate(
        await saveTelephonySettings({
          inbound: {
            hours: { enabled, timezone, days, voicemail, voicemailPrompt, closedPrompt },
          },
        }),
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  // The saved switches, not the ones being edited: these describe what the
  // server is running on right now, and an unsaved tick must not colour them.
  const inboundOn = status?.inbound.ready ?? false;
  const drifted = preview ? (voicemail ? preview.voicemailDrifted : preview.closedDrifted) : false;
  const suggestion = preview ? (voicemail ? preview.voicemailText : preview.closedText) : "";
  const silent = !prompt.audioUrl.trim() && !prompt.text.trim();
  // A text with no recording behind it, in a language Twilio has no voice for.
  // The server owns that list, so the answer comes from the status rather than
  // from a second copy of it here.
  const needsClip =
    !prompt.audioUrl.trim() &&
    prompt.text.trim().length > 0 &&
    status !== null &&
    !status.notice.languageVoiceable;
  // The two speech-provider states this card has to repeat, even though the
  // provider tab already shows them.
  //
  // Every OTHER announcement in this module ships with a recorded Estonian
  // fallback, so a synthesis service that cannot produce a clip leaves those
  // operators with a working phone and a wish. This one has no fallback and
  // could not have - it names one office's opening hours - so the difference
  // here is between an announcement and silence, and it belongs next to the
  // button that is about to fail.
  //
  // `ttsReady` is a KEY check and nothing more (`providers.ts`,
  // `isTtsKeyed`): on the free provider it is always true, because there is no
  // credential whose absence could be reported. So this says exactly that, and
  // the other failure - the service being down - is left to
  // `settings.notice.clip.offlineList`, which the clip row below already raises
  // off the live voice listing and which no status field could answer without a
  // network call.
  // The number a night caller is sent to instead of hearing any of this, or ""
  // when nothing is forwarding. The rule and the number both live on the
  // forwarding tab; this card only has to stop claiming otherwise.
  const divert = status?.inbound.divert;
  const diverted = divert?.afterHours && divert.to ? divert.to : "";
  const noTtsKey = status !== null && !status.providers.ttsReady;
  const wrongLanguage = status?.providers.ttsModelLacksEstonian ?? false;

  return (
    // The prompt's voice picker is a `Pop` menu and Settings mounts no `PopHost`
    // of its own; without one the menu is a button that never opens.
    <PopHost>
      <IntegrationCard
        icon={CalendarClock}
        title={t("settings.hours.title")}
        desc={t("settings.hours.desc")}
        status={
          <ConnStatus
            ok={(status?.inbound.hours.enabled ?? false) && inboundOn}
            okLabel={t("settings.hours.statusOn")}
            offLabel={t("settings.hours.statusOff")}
          />
        }
      >
        <form onSubmit={handleSave} className="space-y-5">
          <Note tone="info">{t("settings.hours.about")}</Note>
          {/* The schedule only ever acts on calls the number actually answers.
              Said here rather than left to be discovered by a night that changed
              nothing. */}
          {enabled && !inboundOn ? <Note tone="warn">{t("settings.hours.needsInbound")}</Note> : null}

          <ToggleRow
            label={t("settings.hours.enable")}
            hint={t("settings.hours.enableHint")}
            checked={enabled}
            onChange={setEnabled}
          />

          <FieldGroup title={t("settings.hours.scheduleGroup")}>
            <Field label={t("settings.hours.timezone")} hint={t("settings.hours.timezoneHint")}>
              <TimezoneCombobox
                className="h-11"
                value={timezone}
                onChange={setTimezone}
                aria-label={t("settings.hours.timezone")}
                searchPlaceholder={t("common.timezoneSearch")}
                emptyLabel={t("common.timezoneNone")}
              />
            </Field>

            {/* What time it is where the server thinks the office is. A zone in
                a field is an abstraction until something says the hour back. */}
            {preview ? (
              <Note tone={preview.openNow ? "ok" : "mut"}>
                {t(preview.openNow ? "settings.hours.openNow" : "settings.hours.closedNow", {
                  time: preview.localTime,
                  zone: preview.timezone,
                })}
              </Note>
            ) : null}

            <HoursSchedule days={days} onChange={setDays} />
          </FieldGroup>

          <FieldGroup title={t("settings.hours.announcementGroup")}>
            <Note tone="mut">{t("settings.hours.announcementHint")}</Note>

            {/* Everything below this line describes what a night caller hears -
                and with forwarding armed, most of them will not hear it at all.
                Said here rather than left to be discovered by recording an
                announcement nobody reaches. */}
            {diverted ? (
              <Note tone="warn">
                {t("settings.hours.divertedInstead", { phone: formatPhone(diverted) })}
              </Note>
            ) : null}

            <ToggleRow
              label={t("settings.hours.voicemail")}
              hint={t("settings.hours.voicemailHint")}
              checked={voicemail}
              onChange={setVoicemail}
            />

            {/* The sentence the operator's own schedule produces, and one press
                to put it in the field. Not written automatically: a schedule
                that rewrites the announcement behind somebody's back would put a
                sentence nobody approved on the phone. */}
            {suggestion ? (
              <div className="flex flex-wrap items-center gap-2">
                <Btn
                  onClick={() => setPrompt({ ...prompt, text: suggestion })}
                  disabled={prompt.text.trim() === suggestion}
                >
                  <Wand2 className="size-3.5" />
                  {t("settings.hours.fillFromSchedule")}
                </Btn>
                <span className="crm-sub text-[11.5px] leading-snug">
                  {t("settings.hours.fillHint")}
                </span>
              </div>
            ) : null}

            {/* The one thing neither the audio nor the text can say by itself:
                that the sentence names hours the schedule above no longer keeps. */}
            {drifted ? <Note tone="warn">{t("settings.hours.drifted")}</Note> : null}

            <NoticeVariantFields
              slot={slot}
              value={prompt}
              onChange={setPrompt}
              savedClip={status?.clips[slot]}
              builtInAudio={status?.builtInAudio[slot] ?? ""}
            />

            {/* Switched on with nothing to say ends the call politely and in
                silence, which sounds exactly like the feature being off - the
                one state that cannot be found by ringing at the wrong hour. */}
            {enabled && silent ? <Note tone="bad">{t("settings.hours.silent")}</Note> : null}
            {needsClip ? <Note tone="warn">{t("settings.hours.needsClip")}</Note> : null}
            {/* Beside the button rather than only on the provider tab: there is
                no shipped recording to fall back on here, so a synthesis
                service that cannot be reached for a key means this announcement
                cannot be made at all. */}
            {noTtsKey ? <Note tone="warn">{t("settings.hours.ttsNoKey")}</Note> : null}
            {/* The silent one. The wrong synthesis model returns fluent audio of
                Estonian words in another language's mouth - it previews
                perfectly and is only wrong on somebody's phone. */}
            {wrongLanguage ? <Note tone="warn">{t("settings.hours.ttsWrongLanguage")}</Note> : null}
          </FieldGroup>

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {saveState === "saving" ? t("settings.hours.saving") : t("settings.hours.save")}
            </Btn>
            {saveState === "saved" ? <StatusPill tone="ok">{t("settings.hours.saved")}</StatusPill> : null}
            {saveState === "error" ? <StatusPill tone="lt">{t("settings.hours.saveError")}</StatusPill> : null}
          </SaveRow>
        </form>
      </IntegrationCard>
    </PopHost>
  );
}
